import React, { useState, useEffect } from 'react';
import moment from 'moment';
import numeral from 'numeral';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@material-ui/core/styles';

import { twoDigits, makeGraph } from 'lib/commonLib';

import CurrentStatus from 'components/atoms/CurrentStatus';
import OutlinedButton from 'components/atoms/OutlinedButton';
import LabelNvalue from 'components/atoms/LabelNvalue';
import ChartButtonWrap from 'components/molecules/ChartButtonWrap';
import ContentsWrap from 'components/molecules/ContentsWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import SwitchingArea from 'components/organisms/SwitchingArea';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';

const BarGraph = (props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    params,
    graphList = [],
    infoMessage = t('까지의 통계 그래프입니다.', {
      date: t('term', {
        startDate: moment(params.sDate).format('YYYY-MM-DD'),
        endDate: moment(params.eDate).format('YYYY-MM-DD'),
      }),
    }),
    barData,
    status,
    title,
    labelType = 'MMDD',
    sumCount = [],
    disabled = [],
  } = props;

  const [curGraphComponent, setCurGraphComponent] = useState(false);
  const [graphIndex, setGraphIndex] = useState(0);

  useEffect(() => {
    if (status !== 'loading') {
      handleChangeGraph(graphList[0], 0);
    }
    setGraphIndex(0);
    // eslint-disable-next-line
  }, [barData, status]);

  // 월 일 or 일 반환
  const makeLabel = (onlyDay) => (originalDate) => {
    let label;
    if (typeof originalDate === 'undefined') return '';

    let formatStr = 'YYYYMMDD';
    if (originalDate.length === 6) {
      formatStr = 'YYYYMM';
    }

    const dateObj = moment(originalDate, formatStr);
    const month = twoDigits(dateObj.month() + 1);
    const year = twoDigits(dateObj.year());
    const date = dateObj.date();
    // const day = dateObj.day();

    if (originalDate.length === 6) {
      label =
        labelType === 'YYYY'
          ? `${t(year + '년')}`
          : labelType === 'MM'
          ? `${t(month + '월')}`
          : `${t(year + '년')} ${t(month + '월')}`;
    } else {
      label =
        labelType === 'YYYYMMDD'
          ? `${t(year + '년')} ${t(month + '월')} ${t(date + '일')}`
          : labelType === 'YYYYMM'
          ? `${t(year + '년')} ${t(month + '월')}`
          : labelType === 'MM'
          ? `${t(month + '월')}`
          : labelType === 'MMDD'
          ? `${t(month + '월')} ${twoDigits(date)}${t('일')}`
          : `${twoDigits(date)}${t('일')}`;
    }
    return label;
  };

  const xAxisTick = (tickProps) => {
    const { x, y, payload } = tickProps;
    const { value } = payload;
    const length = String(value).length;
    let dateObj;
    let day;

    if (length === 4) {
      dateObj = moment(value, 'YYYYMM');
    } else {
      dateObj = moment(value, 'YYYYMMDD');
      day = dateObj.day();
    }

    const dayStr = makeLabel(true)(value);
    let fill = '';
    if (day === 0) {
      fill = theme.palette.common.red;
    } else if (day === 6) {
      fill = theme.palette.common.blue;
    }

    return (
      <text
        x={x}
        y={y + 10}
        fill={length !== 8 ? '' : fill}
        textAnchor="middle"
      >
        {dayStr}
      </text>
    );
  };

  const makePopLabel = (date) => {
    const dateObj = moment(date, 'YYYYMMDD');
    const day = dateObj.day();
    const dayStr = makeLabel(false)(date);
    const length = String(date).length;
    let color = '';

    if (length !== 4 && length !== 6) {
      if (day === 0) {
        color = theme.palette.common.red;
      } else if (day === 6) {
        color = theme.palette.common.blue;
      }
    }

    return <span style={{ color: color }}>{dayStr}</span>;
  };

  const handleChangeGraph = (
    {
      title,
      data,
      yAxisData,
      customizeData: customize,
      isPercent = false,
      referenceLine,
    },
    index = 0
  ) => {
    let result;
    setGraphIndex(index);
    if (
      typeof barData[index] !== 'undefined' &&
      JSON.stringify(barData[index]) !== JSON.stringify({})
    ) {
      let primitiveData = barData[index];
      const {
        graphData,
        dataKeys,
        customizeData,
        keyLabel,
        stackId,
        referenceData,
      } = makeGraph({ data, customize, primitiveData, referenceLine });
      console.log(sumCount);
      result = (
        <>
          {sumCount.data.length > 0 && (
            <LabelNvalue
              label={sumCount.label[index]}
              value={
                <>
                  {numeral(sumCount.data[index]).format()}
                  {'원'}
                </>
              }
            />
          )}

          <MultiplexChart
            xAxisTick={(e) => xAxisTick(e)}
            makeLabel={(date) => makePopLabel(date)}
            data={graphData}
            dataKeys={dataKeys}
            customizeData={customizeData}
            keyLabel={keyLabel}
            stackId={stackId.length !== 0 ? stackId.join('$') : null}
            yAxisData={yAxisData}
            legend
            title={title}
            isPercent={isPercent}
            key={title}
            referenceLine={referenceData}
          />
        </>
      );
    } else if (
      barData[index] === undefined ||
      JSON.stringify(barData[index]) === JSON.stringify({})
    ) {
      result = <CurrentStatus type="noData" />;
    }
    setCurGraphComponent(result);
  };
  return (
    <SwitchingArea
      title={
        <InfoTitleArea
          title={title}
          tooltipTitle={infoMessage}
          iconType="fainfoCircle"
          iconColor="graph"
        />
      }
    >
      {status !== '' ? (
        <CurrentStatus type={status} />
      ) : (
        <>
          <ChartButtonWrap>
            {graphList.map((item, index) => {
              return (
                <OutlinedButton
                  key={index}
                  onClick={() => handleChangeGraph(item, index)}
                  selected={graphIndex === index}
                  disabled={disabled.includes(index)}
                >
                  {item.title}
                </OutlinedButton>
              );
            })}
          </ChartButtonWrap>
          <ContentsWrap>{curGraphComponent}</ContentsWrap>
        </>
      )}
    </SwitchingArea>
  );
};

export default BarGraph;
